/**
 * csrf.ts - single source of truth for the Sanctum XSRF-TOKEN cookie.
 *
 * The XSRF-TOKEN cookie is shared browser state (Domain=.gluska.co), so every
 * API client must read and refresh it through this module. Having two clients
 * (ApiClient and BaseApiClient) each with their own cookie parser and refresh
 * logic is how stale-token 419s survived multiple "fixes".
 */
import axios from 'axios'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api'

// Sanctum's csrf-cookie route lives at the API origin, NOT under the /api
// prefix. Derive it via URL.origin — the old API_URL.replace('/api', '')
// corrupted "https://api.gluska.co/api" into "https:/.gluska.co/api" (the
// unanchored replace matches the "/api" inside "//api.gluska.co"), so every
// production token refresh hit a nonexistent host and failed silently.
const CSRF_COOKIE_URL = `${new URL(API_URL, window.location.origin).origin}/sanctum/csrf-cookie`

/**
 * Read the freshest XSRF-TOKEN value, URL-decoded and ready for the
 * X-XSRF-TOKEN header. document.cookie can hold duplicates (a stale
 * host-scoped cookie alongside the domain-scoped one Sanctum sets); browsers
 * list same-path cookies oldest-first, so the last occurrence is the freshest.
 * The previous parser returned null whenever duplicates existed, which
 * dropped the header entirely and made every write 419.
 */
export function readXsrfCookie(): string | null {
  const matches = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]*)/g)
  if (!matches || matches.length === 0) return null
  const last = matches[matches.length - 1]
  const value = last.slice(last.indexOf('=') + 1)
  return value ? decodeURIComponent(value) : null
}

/**
 * Expire every deletable XSRF-TOKEN variant so exactly one cookie exists
 * after the next refresh — a stale duplicate would otherwise shadow the
 * fresh token in document.cookie.
 */
function clearXsrfCookies(): void {
  const host = window.location.hostname
  const parentDomain = '.' + host.split('.').slice(-2).join('.')
  for (const domain of ['', host, parentDomain]) {
    document.cookie = `XSRF-TOKEN=; Max-Age=0; path=/${domain ? `; domain=${domain}` : ''}`
  }
}

let refreshInFlight: Promise<void> | null = null

/**
 * Fetch a fresh XSRF-TOKEN cookie from Sanctum. Concurrent callers share one
 * request — unguarded parallel refreshes let one caller's cookie purge race
 * another caller's header read. Throws on failure so callers know the token
 * is still stale instead of retrying a doomed request.
 */
export function refreshXsrfCookie(): Promise<void> {
  refreshInFlight ??= (async () => {
    clearXsrfCookies()
    await axios.get(CSRF_COOKIE_URL, {
      withCredentials: true,
      headers: { Accept: 'application/json' },
    })
  })().finally(() => {
    refreshInFlight = null
  })
  return refreshInFlight
}

/**
 * Guarantee a token exists before a mutation. Cookie presence is the source
 * of truth: the cookie expires with the session (120 min), so a long-idle
 * tab's first write would otherwise go out without a token and 419. An
 * in-memory "initialized" flag can't see that expiry, which is exactly how
 * idle tabs kept producing CSRF toasts.
 */
export async function ensureXsrfCookie(): Promise<void> {
  if (!readXsrfCookie()) {
    await refreshXsrfCookie()
  }
}
